import React, { useEffect, useMemo, useRef } from 'react';

import { Box, Flex } from '@chakra-ui/react';
import { Button } from '@mesh.ai/rabbit';
import { useQueryClient } from '@tanstack/react-query';
import useMutationObserver from 'Deer/hooks/useMutationObserver';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import useOrganisationSettings from 'components/hooks/useOrganisationSettings';
import useUnsavedDataAlert from 'components/hooks/useUnsavedDataAlert';
import queryString from 'query-string';
import { useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import Sticky from 'react-sticky-el';
import { useSelectedView } from 'reducers/viewsSlice';
import { GoalsObjectiveGenericTreeViewTable } from 'screens/Team/Rabbit/Goals/AgGridTreeView/GoalsObjectiveGenericTreeViewTable';
import {
  goalsCascadedViewReactQueryKey,
  useGoalsCascadedViewData,
} from 'screens/Team/Rabbit/Goals/AgGridTreeView/hooks/dataHooks';
import 'screens/Team/Rabbit/Goals/AgGridTreeView/styles/TreeView.css';

import { ErrorOutline, InfoWorkZero, SaveAndExit } from 'assets/assetsV1/iconComponents';

import { MODULE_KEYS_USER_PREFERENCE_PERSISTENCE } from 'constants/ProjectConstants';
import { StringModifier, modifyString } from 'constants/copyHelpers';
import { getHumanReadableDate, getPersistenceKey, roundOffToPlaces } from 'constants/helper';
import {
  sendClickedOnCancelBulkCheckInCompany,
  sendClickedOnCancelBulkCheckInDirectTeam,
  sendClickedOnCancelBulkCheckInMe,
  sendClickedOnCancelBulkCheckInSkipTeam,
  sendClickedOnSaveBulkCheckInCompany,
  sendClickedOnSaveBulkCheckInDirectTeam,
  sendClickedOnSaveBulkCheckInME,
  sendClickedOnSaveBulkCheckInSkipTeam,
} from 'constants/mixpanelHelper';

import { TextLabel } from 'uiToolkitV1/Typography';

import { useBulkCheckIn } from '../hooks/useBulkCheckIn';
import { setEditableRowData, setInitialRowData, useSelectEditableRowData } from '../slice/CheckInIntentSlice';
import { GoalsIntentAgGridMenu } from './GoalsIntentAgGridMenu';

const GROUP_BY = {
  uuid: null,
  keyName: 'objective',
  displayName: 'Objective',
  enabled: true,
};

const SPLIT_BY = {
  uuid: null,
  keyName: 'none',
  displayName: 'None',
  enabled: true,
};

// eslint-disable-next-line no-shadow
export enum COHORT_ID {
  ME = -12,
  DIRECT_TEAM = -2,
  SKIP_TEAM = -3,
  COMPANY = -1,
}

export const MAX_INLINE_CHECK_IN_COMMENT_LENGTH = 1000;

const sendClickedOnSaveBulkCheckInEvent = (selectedCohortId: number) => {
  switch (selectedCohortId) {
    case COHORT_ID.ME:
      sendClickedOnSaveBulkCheckInME();
      break;
    case COHORT_ID.DIRECT_TEAM:
      sendClickedOnSaveBulkCheckInDirectTeam();
      break;
    case COHORT_ID.SKIP_TEAM:
      sendClickedOnSaveBulkCheckInSkipTeam();
      break;
    case COHORT_ID.COMPANY:
      sendClickedOnSaveBulkCheckInCompany();
      break;
    default:
      break;
  }
};

const sendClickedOnCancelBulkCheckInEvent = (selectedCohortId: number) => {
  switch (selectedCohortId) {
    case COHORT_ID.ME:
      sendClickedOnCancelBulkCheckInMe();
      break;
    case COHORT_ID.DIRECT_TEAM:
      sendClickedOnCancelBulkCheckInDirectTeam();
      break;
    case COHORT_ID.SKIP_TEAM:
      sendClickedOnCancelBulkCheckInSkipTeam();
      break;
    case COHORT_ID.COMPANY:
      sendClickedOnCancelBulkCheckInCompany();
      break;
    default:
      break;
  }
};

const FooterBanner = ({
  uploadStatus = {},
  hasError = false,
  hasInvalidComment = false,
}: {
  hasError: boolean;
  hasInvalidComment: boolean;
  uploadStatus?: {
    totalRecords?: number;
    recordsUpdated?: number;
    recordsFailed?: number;
    status?: 'IN_PROGRESS' | 'ERROR' | 'VALIDATING' | 'FINISHED';
    updatedTS?: number;
    uuid?: string;
  };
}) => {
  const { recordsUpdated = 0, updatedTS, recordsFailed = 0 } = uploadStatus;

  const lastCheckInDate = updatedTS;

  const { moduleName } = useOrganisationSettings();

  const getModuleName = (recordsUpdated: number): string => {
    const isSingular = recordsUpdated === 1;
    return modifyString(moduleName, isSingular ? StringModifier.SENTENCE_CASE : StringModifier.PLURAL_SENTENCE_CASE);
  };

  if (hasError || hasInvalidComment) {
    return (
      <Box
        display="flex"
        padding="12px"
        alignItems="center"
        gap="8px"
        background="#FFFBF6"
        boxShadow="0px -1px 2px 0px rgba(0, 0, 0, 0.08)"
      >
        <ErrorOutline boxSize="24px" height="24px" color="#C62C02" />
        {hasError ? (
          <TextLabel color="#C62C02">Some inputs are invalid or empty. Please make changes and then save.</TextLabel>
        ) : (
          <TextLabel color="#C62C02">
            One or more comments has exceeded the character limit of {MAX_INLINE_CHECK_IN_COMMENT_LENGTH}. Please make
            changes and then save..
          </TextLabel>
        )}
      </Box>
    );
  }

  return (
    <Box
      display="flex"
      padding="12px"
      alignItems="center"
      gap="8px"
      background="#FDFDFD"
      boxShadow="0px -1px 2px 0px rgba(0, 0, 0, 0.08)"
    >
      <InfoWorkZero boxSize="24px" height="24px" color="#666" />
      {recordsFailed <= 0 ? (
        <TextLabel color="#666">
          Last update made was on {getHumanReadableDate(lastCheckInDate)}, {recordsUpdated}{' '}
          {getModuleName(recordsUpdated)} updated successfully.{' '}
        </TextLabel>
      ) : (
        <TextLabel color="#666">
          Last update made was on {getHumanReadableDate(lastCheckInDate)}, {recordsUpdated}{' '}
          {getModuleName(recordsUpdated)} updated successfully, {recordsFailed} failed
        </TextLabel>
      )}
    </Box>
  );
};

const CheckInGoalsTable = ({
  attributeFilter,
  timeBoundPeriod,
  isHierarchyView,
  gridRef,
  groupBy = GROUP_BY,
  pageType = 'TEAM_HUB',
  hasImplicitDepartmentFilter = false,
  setChartData = () => {},
  showParentObjectives = false,
  setRowData = () => {},
  onModalUpdated = () => {},
  setShowLoadingScreen,
  setRefetchUploadStatusToggle,
  uploadStatus,
  selectedTimelines,
  ownership,
}) => {
  const sideNavSelectedCohort = useSelectedView();
  const viewId = sideNavSelectedCohort?.id;
  const gridApi = gridRef?.current?.api;

  const selectedCohortId = sideNavSelectedCohort?.id;

  const { data, isLoading } = useGoalsCascadedViewData({
    attributeFilter,
    cohort: sideNavSelectedCohort,
    groupByKeys: groupBy,
    splitByKeys: SPLIT_BY,
    orderBy: null,
    timeBounds: [timeBoundPeriod],
    isHierarchyView,
    pageType,
    mergeColumns: selectedCohortId !== -12,
    ownership,
  });

  const editableRowData = useSelectEditableRowData();
  const initialRowData = data || [];
  const dispatch = useDispatch();

  const editedRows = editableRowData.filter((row, index) => {
    const { progressLevel, closureStatus, achieved, newComment = '' } = row;
    const {
      progressLevel: initialProgressLevel,
      closureStatus: initialClosureStatus,
      achieved: initialAchieved,
      newComment: initialNewComment = '',
    } = initialRowData[index] || {};

    const isRowEdited =
      progressLevel !== initialProgressLevel ||
      closureStatus !== initialClosureStatus ||
      roundOffToPlaces(+achieved, 2) !== roundOffToPlaces(initialAchieved, 2) ||
      achieved === '' ||
      newComment !== initialNewComment;

    return isRowEdited;
  });

  const hasError = editedRows.some((row) => row.hasError);
  const hasInvalidComment = editedRows.some((row) => row.newComment?.length > MAX_INLINE_CHECK_IN_COMMENT_LENGTH);
  const totalEditedRows = editedRows.length;
  const history = useHistory();

  const queryClient = useQueryClient();
  const { pathname: curPathName, search: curSearch } = useLocation();

  const { showUnsavedDataAlertDialog } = useUnsavedDataAlert();

  useEffect(() => {
    // Block navigation and register a callback that
    // fires when a navigation attempt is blocked.\
    let unblock;
    // comparing editedRows.length with initialRowData.length for the edge case where user is coming from company goals to checkin in my data goals,
    // During initial render editedRows  contains data from company goals and initialRowData contains data from my data goals. It makes the initial totalEditedRows values to be wrong.
    if (totalEditedRows > 0 && editableRowData.length === initialRowData.length) {
      unblock = history.block((tx) => {
        const { type, showTaskDetails, objectivePId } = queryString.parse(curSearch);
        const {
          type: nextType,
          showTaskDetails: nextShowTaskDetails,
          objectivePId: nextObjectivePId,
        } = queryString.parse(tx.search);

        const isKeyResult =
          (showTaskDetails === 'true' && type === 'GOAL') || (nextShowTaskDetails === 'true' && nextType === 'GOAL');
        const isObjective = objectivePId || nextObjectivePId;

        if (tx.pathname === curPathName && (isObjective || isKeyResult)) {
          return;
        }

        showUnsavedDataAlertDialog({
          onCancelCallback: () => {
            dispatch(
              setEditableRowData({
                rowData: data ? data.map((row) => ({ ...row, achieved: roundOffToPlaces(row?.achieved, 2) })) : [],
              })
            );
            unblock();
            history.push({ pathname: tx.pathname, search: tx.search });
          },
        });
        // eslint-disable-next-line consistent-return
        return false;
      });
    }
    return () => {
      // Unregister the block callback.
      if (unblock) {
        unblock();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history, totalEditedRows, curSearch]);

  const refetchCascadedViewData = async () => {
    await queryClient.invalidateQueries(
      goalsCascadedViewReactQueryKey({
        cohort: sideNavSelectedCohort,
        groupByKeys: groupBy,
        splitByKeys: SPLIT_BY,
        orderBy: null,
        timeBounds: [timeBoundPeriod],
        attributeFilter,
        isHierarchyView,
        pageType,
        ownership,
      })
    );
  };

  const resetEditedRows = () => {
    dispatch(
      setEditableRowData({
        rowData: data ? data.map((row) => ({ ...row, achieved: roundOffToPlaces(row?.achieved, 2) })) : [],
      })
    );
    setShowLoadingScreen(true);
    setRefetchUploadStatusToggle((prev) => !prev);
  };

  const { mutate: saveEditedRows } = useBulkCheckIn(
    async () => {
      await refetchCascadedViewData();
      setShowLoadingScreen(true);
      resetEditedRows();
    },
    () => {
      setShowLoadingScreen(false);
    }
  );

  /* Grid State persistence Key */
  const gridStatePersistenceKey = useMemo(() => {
    const moduleName = MODULE_KEYS_USER_PREFERENCE_PERSISTENCE.GOAL;
    const tabId = 'Explore';
    const uniqueId = showParentObjectives ? 'ExploreGrid' : 'ListGrid';

    return getPersistenceKey({
      moduleName,
      viewId,
      tabId,
      uniqueId,
    });
  }, [viewId, showParentObjectives]);

  useEffect(() => {
    setChartData((prev) => ({ ...prev, isLoading, rowData: data }));
    if (gridApi) {
      if (isLoading) {
        gridApi.setGridOption('loading', true);
      } else if (data && data.length === 0) {
        gridApi.showNoRowsOverlay();
      } else {
        gridApi.setGridOption('loading', false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, data, gridApi]);
  // Issue with React-Sticky-El, mimicking the scroll to fix the issue
  // https://github.com/gm0t/react-sticky-el/issues/63
  const ref = useRef(document.body);
  useMutationObserver(ref, () => {
    window.scrollTo(window.scrollX, window.scrollY - 1);
    window.scrollTo(window.scrollX, window.scrollY + 1);
  });

  useEffect(() => {
    setRowData(data);
    dispatch(setInitialRowData({ rowData: data }));
  }, [data, dispatch, setRowData]);

  return (
    <Box position="relative">
      <Box p={3} paddingBottom="0px">
        <GoalsIntentAgGridMenu
          gridRef={gridRef}
          refetchCascadedViewData={refetchCascadedViewData}
          selectedTimelines={selectedTimelines}
          showAddButton={false}
        />
        <GoalsObjectiveGenericTreeViewTable
          gridRef={gridRef}
          key={gridStatePersistenceKey}
          gridStatePersistenceKey={gridStatePersistenceKey}
          data={data}
          refetchHandler={refetchCascadedViewData}
          hasImplicitDepartmentFilter={hasImplicitDepartmentFilter}
          setChartData={setChartData}
          onModalUpdated={onModalUpdated}
          source="Goals"
          isCheckIn
        />
      </Box>

      <Sticky mode="bottom">
        {/* @ts-expect-error : union too complex */}
        <Box display="flex" flexDirection="column">
          {(hasError || uploadStatus?.status === 'FINISHED' || uploadStatus?.status === 'ERROR') && (
            <FooterBanner uploadStatus={uploadStatus} hasError={hasError} hasInvalidComment={hasInvalidComment} />
          )}
          {/*  @ts-expect-error : union too complex */}
          <Flex
            padding="12px 18px"
            justifyContent="space-between"
            alignItems="center"
            borderRadius="0px 0px 8px 8px"
            background="#F5F7F9"
            boxShadow="0px -1px 0px 0px #CFD7DF"
          >
            <Button
              variant="outline"
              style={{ background: 'transparent' }}
              onClick={() => {
                sendClickedOnCancelBulkCheckInEvent(selectedCohortId);
                if (totalEditedRows === 0) {
                  return;
                }
                showUnsavedDataAlertDialog({
                  onCancelCallback: () => {
                    dispatch(
                      setEditableRowData({
                        rowData: data
                          ? data.map((row) => ({ ...row, achieved: roundOffToPlaces(row?.achieved, 2) }))
                          : [],
                      })
                    );
                  },
                });
              }}
            >
              Cancel
            </Button>
            <Flex>
              <Flex alignItems="center" marginRight="16px">
                <TextLabel fontWeight="600" marginRight="4px">
                  {totalEditedRows}
                </TextLabel>
                {/* Make checkins singular for 0 and 1 */}
                {totalEditedRows === 1 ? (
                  <TextLabel>Check-in completed</TextLabel>
                ) : (
                  <TextLabel>Check-ins completed</TextLabel>
                )}
              </Flex>
              <Button
                // eslint-disable-next-line react/no-unstable-nested-components
                iconLeft={() => <SaveAndExit width="24px" height="24px" color="white" />}
                disabled={totalEditedRows === 0 || hasError || hasInvalidComment}
                onClick={() => {
                  saveEditedRows(editedRows);

                  sendClickedOnSaveBulkCheckInEvent(selectedCohortId);
                }}
              >
                Save
              </Button>
            </Flex>
          </Flex>
        </Box>
      </Sticky>
    </Box>
  );
};

export { CheckInGoalsTable };
